/**
 * Reconciliation Dashboard Component
 * Period-based financial reconciliation between receipts and payouts
 * Trust Marketplace - Phase 2: Financial Ledger System
 */

import React, { useState, useEffect } from 'react';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import PassThroughStatement from '@/components/financial/PassThroughStatement';
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Calendar,
  FileText,
  Download,
  Plus,
  Eye,
  RefreshCw
} from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface ReconciliationDashboardProps {
  programId?: number;
  sponsorId?: number;
}

interface ReconciliationPeriod {
  id: number;
  reconciliation_number: string;
  period_start_date: string;
  period_end_date: string;
  total_receipts: number;
  total_payouts: number;
  variance: number;
  variance_percentage: number;
  status: string;
  reconciled_by?: string;
  reconciled_date?: string;
  notes?: string;
  created_at: string;
}

interface ReconciliationLineItem {
  id: number;
  reconciliation_id: number;
  item_type: string;
  item_id: number;
  item_number: string;
  item_date: string;
  amount: number;
  description?: string;
  matched: boolean;
}

interface PaymentMethodBreakdown {
  payment_method: string;
  count: number;
  total_amount: number;
}

interface BankAccountBreakdown {
  bank_account_name: string;
  bank_account_last4: string;
  count: number;
  total_amount: number;
}

const RECONCILIATION_PERIODS = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'CUSTOM', label: 'Custom Period' }
];

const RECONCILIATION_STATUSES = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'BALANCED', label: 'Balanced' },
  { value: 'UNBALANCED', label: 'Unbalanced' },
  { value: 'APPROVED', label: 'Approved' }
];

const ReconciliationDashboard: React.FC<ReconciliationDashboardProps> = ({ programId, sponsorId }) => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const { fmt } = useFormattedDate();
  const [reconciliations, setReconciliations] = useState<ReconciliationPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterPeriod, setFilterPeriod] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedReconciliation, setSelectedReconciliation] = useState<ReconciliationPeriod | null>(null);
  const [lineItems, setLineItems] = useState<ReconciliationLineItem[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Payment method breakdown
  const [paymentMethodBreakdown, setPaymentMethodBreakdown] = useState<PaymentMethodBreakdown[]>([]);
  const [bankAccountBreakdown, setBankAccountBreakdown] = useState<BankAccountBreakdown[]>([]);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  // Create form state
  const [newReconciliation, setNewReconciliation] = useState({
    period_type: 'MONTHLY',
    period_start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    period_end_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    fetchReconciliations();
  }, [programId, sponsorId, filterPeriod, filterStatus]);

  const fetchReconciliations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (programId) params.append('program_id', programId.toString());
      if (sponsorId) params.append('sponsor_id', sponsorId.toString());
      if (filterPeriod) params.append('period_type', filterPeriod);
      if (filterStatus) params.append('status', filterStatus);

      const url = getWeaveinApiUrl(`/api/financial-reconciliation?${params.toString()}`);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch reconciliations: ${response.status}`);
      }

      const data = await response.json();
      setReconciliations(data);
    } catch (err) {
      console.error('Error fetching reconciliations:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchLineItems = async (reconciliationId: number) => {
    try {
      setLoadingDetails(true);
      const url = getWeaveinApiUrl(`/api/financial-reconciliation/${reconciliationId}/line-items`);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch line items');
      }

      const data = await response.json();
      setLineItems(data);
    } catch (err) {
      console.error('Error fetching line items:', err);
      alert('Failed to load reconciliation details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchPaymentBreakdown = async (reconciliationId: number) => {
    try {
      setLoadingBreakdown(true);

      // Fetch payment method breakdown
      const payoutUrl = getWeaveinApiUrl(`/api/financial-reconciliation/${reconciliationId}/payout-breakdown`);
      const payoutResponse = await authFetch(payoutUrl);

      if (payoutResponse.ok) {
        const payoutData = await payoutResponse.json();
        setPaymentMethodBreakdown(payoutData);
      }

      // Fetch bank account breakdown
      const depositUrl = getWeaveinApiUrl(`/api/financial-reconciliation/${reconciliationId}/deposit-breakdown`);
      const depositResponse = await authFetch(depositUrl);

      if (depositResponse.ok) {
        const depositData = await depositResponse.json();
        setBankAccountBreakdown(depositData);
      }
    } catch (err) {
      console.error('Error fetching payment breakdown:', err);
      // Don't alert on failure, just log it
    } finally {
      setLoadingBreakdown(false);
    }
  };

  const createReconciliation = async () => {
    try {
      const url = getWeaveinApiUrl('/api/financial-reconciliation');

      const response = await authFetch(url, {
        method: 'POST',
        jsonBody: newReconciliation
      });

      if (!response.ok) {
        throw new Error('Failed to create reconciliation');
      }

      // Reset form and close modal
      setNewReconciliation({
        period_type: 'MONTHLY',
        period_start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        period_end_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
        notes: ''
      });
      setIsCreateModalOpen(false);
      fetchReconciliations();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create reconciliation');
    }
  };

  const viewDetails = async (reconciliation: ReconciliationPeriod) => {
    setSelectedReconciliation(reconciliation);
    await Promise.all([
      fetchLineItems(reconciliation.id),
      fetchPaymentBreakdown(reconciliation.id)
    ]);
  };

  const getStatusColor = (status: string): string => {
    switch (status.toUpperCase()) {
      case 'BALANCED':
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'UNBALANCED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getVarianceColor = (variance: number): string => {
    if (Math.abs(variance) < 0.01) return 'text-green-600';
    if (Math.abs(variance) < 100) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return fmt.medium(dateString);
  };

  // Calculate summary metrics
  const totalReconciliations = reconciliations.length;
  const balancedCount = reconciliations.filter(r => r.status === 'BALANCED' || r.status === 'APPROVED').length;
  const unbalancedCount = reconciliations.filter(r => r.status === 'UNBALANCED').length;
  const totalVariance = reconciliations.reduce((sum, r) => sum + Math.abs(r.variance), 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <p className="text-red-800">Error: {error}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Per-org pass-through statement (service fees + provider costs) */}
      <PassThroughStatement />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && <h2 className="text-2xl font-bold text-gray-900">{t('financial.financial_reconciliation', 'Financial Reconciliation')}</h2>}
          <p className="text-sm text-gray-600 mt-1">{t('financial.periodbased_reconciliation_of_receipts_and', 'Period-based reconciliation of receipts and payouts')}</p>
        </div>

        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button className="bg-v2accent hover:bg-v2accent/90">
              <Plus className="w-4 h-4 mr-2" />
              {t('financial.new_reconciliation', 'New Reconciliation')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('financial.create_new_reconciliation', 'Create New Reconciliation')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="period_type">{t('financial.period_type', 'Period Type')}</Label>
                <Select
                  value={newReconciliation.period_type}
                  onValueChange={(value) => setNewReconciliation({ ...newReconciliation, period_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECONCILIATION_PERIODS.map(period => (
                      <SelectItem key={period.value} value={period.value}>
                        {period.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="period_start_date">{t('financial.start_date', 'Start Date')}</Label>
                  <Input
                    id="period_start_date"
                    type="date"
                    value={newReconciliation.period_start_date}
                    onChange={(e) => setNewReconciliation({ ...newReconciliation, period_start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="period_end_date">{t('financial.end_date', 'End Date')}</Label>
                  <Input
                    id="period_end_date"
                    type="date"
                    value={newReconciliation.period_end_date}
                    onChange={(e) => setNewReconciliation({ ...newReconciliation, period_end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                  {t('financial.cancel', 'Cancel')}
                </Button>
                <Button
                  onClick={createReconciliation}
                  className="bg-v2accent hover:bg-v2accent/90"
                >
                  {t('financial.create_reconciliation', 'Create Reconciliation')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-700 font-medium">{t('financial.total_periods', 'Total Periods')}</span>
          </div>
          <div className="text-2xl font-bold text-blue-900">{totalReconciliations}</div>
          <div className="text-xs text-blue-600 mt-1">reconciliations</div>
        </Card>

        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700 font-medium">{t('financial.balanced', 'Balanced')}</span>
          </div>
          <div className="text-2xl font-bold text-green-900">{balancedCount}</div>
          <div className="text-xs text-green-600 mt-1">
            {totalReconciliations > 0 ? Math.round((balancedCount / totalReconciliations) * 100) : 0}% rate
          </div>
        </Card>

        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-xs text-red-700 font-medium">{t('financial.unbalanced', 'Unbalanced')}</span>
          </div>
          <div className="text-2xl font-bold text-red-900">{unbalancedCount}</div>
          <div className="text-xs text-red-600 mt-1">require attention</div>
        </Card>

        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-yellow-600" />
            <span className="text-xs text-yellow-700 font-medium">{t('financial.total_variance', 'Total Variance')}</span>
          </div>
          <div className="text-2xl font-bold text-yellow-900">{formatCurrency(totalVariance)}</div>
          <div className="text-xs text-yellow-600 mt-1">cumulative</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="filter_period">{t('financial.period_type_1', 'Period Type')}</Label>
            <Select value={filterPeriod} onValueChange={setFilterPeriod}>
              <SelectTrigger>
                <SelectValue placeholder={t('financial.all_periods_1', 'All Periods')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t('financial.all_periods', 'All Periods')}</SelectItem>
                {RECONCILIATION_PERIODS.map(period => (
                  <SelectItem key={period.value} value={period.value}>
                    {period.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="filter_status">{t('financial.status', 'Status')}</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t('financial.all_statuses_1', 'All Statuses')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t('financial.all_statuses', 'All Statuses')}</SelectItem>
                {RECONCILIATION_STATUSES.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2 flex items-end gap-2">
            <Button variant="outline" onClick={fetchReconciliations} className="flex-1">
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('financial.refresh', 'Refresh')}
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              {t('financial.export', 'Export')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Reconciliation Periods List */}
      {reconciliations.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('financial.no_reconciliations_yet', 'No reconciliations yet')}</h3>
          <p className="text-sm text-gray-600 mb-4">
            {t('financial.create_your_first_reconciliation_period', 'Create your first reconciliation period to start tracking')}
          </p>
          <Button onClick={() => setIsCreateModalOpen(true)} className="bg-v2accent hover:bg-v2accent/90">
            <Plus className="w-4 h-4 mr-2" />
            {t('financial.create_first_reconciliation', 'Create First Reconciliation')}
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {reconciliations.map(reconciliation => {
            const isBalanced = Math.abs(reconciliation.variance) < 0.01;

            return (
              <Card key={reconciliation.id} className="p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-blue-200">
                    {isBalanced ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-6 h-6 text-yellow-600" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {reconciliation.reconciliation_number}
                          </h4>
                          <Badge className={getStatusColor(reconciliation.status)}>
                            {reconciliation.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(reconciliation.period_start_date)} - {formatDate(reconciliation.period_end_date)}
                          </span>
                        </div>
                      </div>

                      <Dialog>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewDetails(reconciliation)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {t('financial.details', 'Details')}
                        </Button>
                        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>{t('financial.reconciliation_details', 'Reconciliation Details')}</DialogTitle>
                          </DialogHeader>
                          {selectedReconciliation && selectedReconciliation.id === reconciliation.id && (
                            <div className="space-y-6 mt-4">
                              {/* Summary */}
                              <div className="grid grid-cols-3 gap-4">
                                <Card className="p-4 bg-blue-50 border-blue-200">
                                  <div className="text-xs text-blue-700 mb-1">{t('financial.total_receipts', 'Total Receipts')}</div>
                                  <div className="text-xl font-bold text-blue-900">
                                    {formatCurrency(selectedReconciliation.total_receipts)}
                                  </div>
                                </Card>
                                <Card className="p-4 bg-green-50 border-green-200">
                                  <div className="text-xs text-green-700 mb-1">{t('financial.total_payouts', 'Total Payouts')}</div>
                                  <div className="text-xl font-bold text-green-900">
                                    {formatCurrency(selectedReconciliation.total_payouts)}
                                  </div>
                                </Card>
                                <Card className={`p-4 ${isBalanced ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                  <div className={`text-xs mb-1 ${isBalanced ? 'text-green-700' : 'text-red-700'}`}>
                                    {t('financial.variance', 'Variance')}
                                  </div>
                                  <div className={`text-xl font-bold ${getVarianceColor(selectedReconciliation.variance)}`}>
                                    {formatCurrency(selectedReconciliation.variance)}
                                  </div>
                                  <div className={`text-xs mt-1 ${isBalanced ? 'text-green-600' : 'text-red-600'}`}>
                                    {selectedReconciliation.variance_percentage.toFixed(2)}%
                                  </div>
                                </Card>
                              </div>

                              {/* Line Items */}
                              {loadingDetails ? (
                                <div className="flex justify-center items-center h-32">
                                  <LoadingSpinner />
                                </div>
                              ) : lineItems.length > 0 ? (
                                <div>
                                  <h4 className="font-semibold mb-3">Line Items ({lineItems.length})</h4>
                                  <div className="border rounded-lg overflow-hidden">
                                    <table className="w-full">
                                      <thead className="bg-gray-50 border-b">
                                        <tr>
                                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">{t('financial.type', 'Type')}</th>
                                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">{t('financial.number', 'Number')}</th>
                                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">{t('financial.date', 'Date')}</th>
                                          <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">{t('financial.amount', 'Amount')}</th>
                                          <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">{t('financial.matched', 'Matched')}</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y">
                                        {lineItems.map(item => (
                                          <tr key={item.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm">
                                              <Badge variant="outline" className="text-xs">
                                                {item.item_type}
                                              </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-sm font-mono">{item.item_number}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{formatDate(item.item_date)}</td>
                                            <td className="px-4 py-3 text-sm text-right font-semibold">
                                              {formatCurrency(item.amount)}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                              {item.matched ? (
                                                <CheckCircle className="w-4 h-4 text-green-600 inline" />
                                              ) : (
                                                <AlertTriangle className="w-4 h-4 text-yellow-600 inline" />
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-center text-gray-500 py-8">{t('financial.no_line_items', 'No line items')}</p>
                              )}

                              {/* Payment Method Breakdown */}
                              {loadingBreakdown ? (
                                <div className="flex justify-center items-center h-32">
                                  <LoadingSpinner />
                                </div>
                              ) : (bankAccountBreakdown.length > 0 || paymentMethodBreakdown.length > 0) && (
                                <div className="grid grid-cols-2 gap-6">
                                  {/* Deposits by Bank Account */}
                                  {bankAccountBreakdown.length > 0 && (
                                    <div>
                                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                                        <TrendingUp className="w-4 h-4 text-blue-600" />
                                        {t('financial.deposits_by_bank_account', 'Deposits by Bank Account')}
                                      </h4>
                                      <div className="space-y-2">
                                        {bankAccountBreakdown.map((item, index) => (
                                          <Card key={index} className="p-3 bg-blue-50 border-blue-200">
                                            <div className="flex items-center justify-between mb-1">
                                              <span className="text-sm font-medium text-gray-900">
                                                {item.bank_account_name}
                                              </span>
                                              <span className="text-sm font-bold text-blue-600">
                                                {formatCurrency(item.total_amount)}
                                              </span>
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-gray-600">
                                              <span>...{item.bank_account_last4}</span>
                                              <span>{item.count} deposits</span>
                                            </div>
                                          </Card>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Payouts by Payment Method */}
                                  {paymentMethodBreakdown.length > 0 && (
                                    <div>
                                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                                        <DollarSign className="w-4 h-4 text-green-600" />
                                        {t('financial.payouts_by_payment_method', 'Payouts by Payment Method')}
                                      </h4>
                                      <div className="space-y-2">
                                        {paymentMethodBreakdown.map((item, index) => (
                                          <Card key={index} className="p-3 bg-green-50 border-green-200">
                                            <div className="flex items-center justify-between mb-1">
                                              <span className="text-sm font-medium text-gray-900">
                                                {item.payment_method.replace('_', ' ')}
                                              </span>
                                              <span className="text-sm font-bold text-green-600">
                                                {formatCurrency(item.total_amount)}
                                              </span>
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-gray-600">
                                              <span>{item.count} payouts</span>
                                              <span>
                                                {selectedReconciliation && selectedReconciliation.total_payouts > 0
                                                  ? ((item.total_amount / selectedReconciliation.total_payouts) * 100).toFixed(1)
                                                  : 0}%
                                              </span>
                                            </div>
                                          </Card>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                    </div>

                    {/* Financial Summary */}
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('financial.receipts', 'Receipts')}</div>
                        <div className="text-sm font-semibold text-blue-600">
                          {formatCurrency(reconciliation.total_receipts)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('financial.payouts', 'Payouts')}</div>
                        <div className="text-sm font-semibold text-green-600">
                          {formatCurrency(reconciliation.total_payouts)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('financial.variance_1', 'Variance')}</div>
                        <div className={`text-sm font-semibold ${getVarianceColor(reconciliation.variance)}`}>
                          {formatCurrency(reconciliation.variance)}
                          <span className="text-xs ml-1">({reconciliation.variance_percentage.toFixed(2)}%)</span>
                        </div>
                      </div>
                    </div>

                    {/* Reconciliation Info */}
                    {reconciliation.reconciled_date && reconciliation.reconciled_by && (
                      <div className="text-xs text-gray-500">
                        Reconciled by {reconciliation.reconciled_by} on {formatDate(reconciliation.reconciled_date)}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReconciliationDashboard;
